'use client'
import { useEffect, useRef, useState } from 'react'
import { F0Box } from '@factorialco/f0-react'

export interface DeviceImageCarouselProps {
  images: string[]
}

export const DeviceImageCarousel: React.FC<DeviceImageCarouselProps> = ({ images }) => {
  const [active, setActive] = useState(0)
  const [previous, setPrevious] = useState<number | null>(null)
  const [isCrossfading, setIsCrossfading] = useState(false)
  const crossfadeTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (crossfadeTimeoutRef.current) {
        window.clearTimeout(crossfadeTimeoutRef.current)
      }
    }
  }, [])

  const handleImageChange = (index: number) => {
    if (index === active) return

    if (crossfadeTimeoutRef.current) {
      window.clearTimeout(crossfadeTimeoutRef.current)
    }

    setPrevious(active)
    setActive(index)
    setIsCrossfading(true)

    window.requestAnimationFrame(() => {
      setIsCrossfading(false)
    })

    crossfadeTimeoutRef.current = window.setTimeout(() => {
      setPrevious(null)
      crossfadeTimeoutRef.current = null
    }, 200)
  }

  if (!images?.length) return null

  return (
    <>
      <F0Box display="flex" flexDirection="row" gap="sm" width="full">
        {images.length > 1 && (
          <div
            style={{ width: '110px', flexShrink: 0, position: 'relative', alignSelf: 'stretch' }}
          >
            <div
              className="flex flex-col gap-2 overflow-y-auto"
              style={{ position: 'absolute', inset: 0 }}
            >
              {images.map((src, index) => (
                <Thumbnail
                  key={`${src}-${index}`}
                  src={src}
                  isActive={index === active}
                  onClick={() => handleImageChange(index)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <F0Box flexDirection="row" alignItems="center" border="default" borderRadius="md">
            <div className="relative w-full" style={{ minHeight: '300px', aspectRatio: '16/9' }}>
              {previous !== null && (
                <img
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-200 ${
                    isCrossfading ? 'opacity-100' : 'opacity-0'
                  }`}
                  src={images[previous]}
                  alt=""
                />
              )}
              <img
                className={`w-full h-full object-contain transition-opacity duration-200 ${
                  isCrossfading ? 'opacity-0' : 'opacity-100'
                }`}
                src={images[active]}
                alt=""
              />
            </div>
          </F0Box>
        </div>
      </F0Box>
    </>
  )
}

function Thumbnail({
  src,
  isActive,
  onClick,
}: {
  src: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <div className="cursor-pointer shrink-0" onClick={onClick}>
      <F0Box
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        border="default"
        borderRadius="md"
        borderColor={isActive ? 'bold' : undefined}
      >
        <img
          src={src}
          alt=""
          className="p-1 w-full object-contain"
          style={{ aspectRatio: '1/1', height: '60px' }}
        />
      </F0Box>
    </div>
  )
}
